export interface FAQContentItem {
  question: string;
  answer: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  body: string[];
}

export interface CareerListing {
  title: string;
  location: string;
  type: string;
  description: string;
}

export interface PressItem {
  date: string;
  title: string;
  outlet: string;
  url?: string;
}

export interface ContactInquiry {
  label: string;
  description: string;
  email: string;
}

export const CONTACT = {
  supportEmail: 'support@fashapp.com',
  pressEmail: 'press@fashapp.com',
  careersEmail: 'careers@fashapp.com',
  responseTime: 'We typically respond within 1–2 business days.',
} as const;

export const CONTACT_INQUIRIES: ContactInquiry[] = [
  {
    label: 'Order & delivery help',
    description: 'Questions about an order, delivery, returns, or disputes.',
    email: CONTACT.supportEmail,
  },
  {
    label: 'Boutique partnerships',
    description: 'Interested in selling on Fash or questions about your merchant account.',
    email: CONTACT.supportEmail,
  },
  {
    label: 'Press & media',
    description: 'Media inquiries, interview requests, and brand assets.',
    email: CONTACT.pressEmail,
  },
  {
    label: 'Careers',
    description: 'Open roles, general applications, and hiring questions.',
    email: CONTACT.careersEmail,
  },
];

export const SHIPPING_SECTIONS: FAQContentItem[] = [
  {
    question: 'How fast is same-day delivery?',
    answer:
      "Most orders are delivered within 1–3 hours of being placed, depending on the boutique's proximity and order volume. You'll see an estimated delivery window at checkout, and you can track your order in real-time once it's been dispatched.",
  },
  {
    question: 'How much does delivery cost?',
    answer:
      'Delivery fees vary based on distance and order size, typically ranging from $5–$15. We frequently offer free delivery promotions — check the homepage for current offers. Orders over $100 often qualify for free same-day delivery.',
  },
  {
    question: 'Can I track my order?',
    answer:
      "Yes! Once your order is confirmed and a driver has been assigned, you can track your delivery in real-time on the order tracking page. You'll see the driver's location on a live map, estimated arrival time, and receive notifications at each stage.",
  },
  {
    question: "What if I'm not home when my order arrives?",
    answer:
      'Our drivers will attempt to contact you when they arrive. You can add delivery instructions at checkout (e.g., "leave with doorman" or "call when outside"). If delivery can\'t be completed, we\'ll work with you to reschedule.',
  },
  {
    question: 'What happens when I receive an order?',
    answer:
      "When your order arrives, the driver will hand it to you or follow your delivery instructions. You'll receive a confirmation notification, and you can rate your delivery experience. If anything is wrong with your order, you can open a dispute directly from the order details page.",
  },
];

export const RETURNS_SECTIONS: FAQContentItem[] = [
  {
    question: "What is Fash's return policy?",
    answer:
      "Return policies are set by each individual boutique and are displayed on the product detail page before you purchase. Most boutiques offer returns or exchanges within 14–30 days for unworn items with tags attached. Check the specific boutique's policy on their Fash storefront.",
  },
  {
    question: 'How do I return an item?',
    answer:
      'To initiate a return, go to your order details and tap "Open Dispute." Describe the issue and our team will coordinate with the boutique to arrange a return or exchange. In many cases, a driver can pick up the return from your location.',
  },
  {
    question: 'What if my order arrives damaged or incorrect?',
    answer:
      "We're sorry if that happens! Open a dispute from your order details page right away and include photos of the issue. Our support team reviews disputes quickly and will work with the boutique to resolve it — whether that means a replacement, exchange, or full refund.",
  },
];

export const CAREERS: CareerListing[] = [
  {
    title: 'Operations Associate',
    location: 'New York, NY',
    type: 'Full-time',
    description:
      'Help coordinate boutique onboarding, order fulfillment, and day-to-day marketplace operations as we expand across NYC neighborhoods.',
  },
  {
    title: 'Software Engineer',
    location: 'New York, NY · Remote-friendly',
    type: 'Full-time',
    description:
      'Build the shopper, merchant, and delivery experiences that power same-day boutique fashion — React, TypeScript, Firebase, and Stripe.',
  },
];

export const PRESS_ITEMS: PressItem[] = [
  {
    date: '2026-07-15',
    title: 'Fash launches same-day delivery from local NYC boutiques',
    outlet: 'Fash Blog',
  },
  {
    date: '2026-06-01',
    title: 'Independent retailers get a new channel for same-day fashion delivery',
    outlet: 'Coming soon',
  },
];

export const PRESS_BLURB =
  'Fash is a fashion marketplace connecting shoppers with independent local boutiques for same-day delivery. We operate in select New York City neighborhoods and are expanding rapidly.';

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: 'welcome-to-fash',
    title: 'Welcome to Fash',
    date: '2026-07-15',
    excerpt:
      'Same-day delivery from your favorite local boutiques — discover what makes Fash different.',
    body: [
      'Fash connects you with curated independent boutiques in your neighborhood for same-day fashion delivery. Browse unique pieces, support local retailers, and get your order delivered to your door — often within hours.',
      'Every boutique on Fash is a verified independent retailer. We personally vet each partner before they join the platform, so you can shop with confidence knowing you are buying directly from the boutique.',
      'We are starting in select New York City neighborhoods — SoHo, the West Village, and Williamsburg — and expanding quickly. Create a free account to save favorites, track orders, and be the first to know when we launch near you.',
    ],
  },
  {
    slug: 'same-day-delivery-explained',
    title: 'Same-Day Delivery, Explained',
    date: '2026-07-21',
    excerpt:
      'How Fash gets boutique fashion from the rack to your door in hours, not days.',
    body: [
      'When you place an order on Fash, the boutique confirms and prepares your items while we dispatch a courier from our delivery network. Most orders arrive within 1–3 hours depending on distance and order volume.',
      'You can track your delivery in real time once a driver is assigned — see their location on a live map and get notified at each stage of the journey.',
      'Delivery fees typically range from $5–$15 based on distance. We regularly run promotions for free delivery, especially on orders over $100. Check the homepage for current offers.',
    ],
  },
];

export function getBlogPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((post) => post.slug === slug);
}
